let imdCache = null;

function normalizePostcode(postcode) {
  return String(postcode || '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .trim();
}

function formatPostcode(postcode) {
  const normalized = normalizePostcode(postcode);
  if (normalized.length <= 3) return normalized;
  return `${normalized.slice(0, -3)} ${normalized.slice(-3)}`;
}

function getPostcodePrefix(postcode) {
  const outwardCode = formatPostcode(postcode).split(' ')[0] || '';
  const baMatch = outwardCode.match(/^BA\d{1,2}/);
  if (baMatch) return baMatch[0];
  const letters = outwardCode.match(/^[A-Z]+/);
  return letters ? letters[0] : outwardCode;
}

function isValidUKPostcode(postcode) {
  const normalized = String(postcode || '').toUpperCase().trim();
  return /^([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})$/.test(normalized);
}

function getDataUrl(fileName) {
  const base = process.env.BASE_URL || '/';
  return `${base.replace(/\/$/, '')}/data/${fileName}`;
}

async function loadJsonFromGzip(url) {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error('Unable to load compressed IMD data');
  }

  // If server already sends Content-Encoding: gzip, browser auto-decompresses it.
  if ((response.headers.get('content-encoding') || '').includes('gzip')) {
    return response.json();
  }

  // If T4 serves .gz as a normal static file, decompress in browser.
  if ('DecompressionStream' in window && response.body) {
    const stream = response.body.pipeThrough(new DecompressionStream('gzip'));
    return new Response(stream).json();
  }

  throw new Error('Browser does not support gzip decompression');
}

async function loadImdData() {
  if (imdCache) return imdCache;

  try {
    imdCache = await loadJsonFromGzip(getDataUrl('imd-data.json.gz'));
  } catch (error) {
    const response = await fetch(getDataUrl('imd-data.json'));
    if (!response.ok) throw new Error('Unable to load IMD data');
    imdCache = await response.json();
  }

  return imdCache;
}

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.postcodes)) return data.postcodes;
  if (data && Array.isArray(data.data)) return data.data;

  // imd-data.json is commonly an object keyed by normalised postcode:
  // { "BS81TH": { "imdQuintile": 1 } }
  // Preserve that key as the postcode so lookup works.
  if (data && typeof data === 'object') {
    return Object.keys(data).map((postcode) => ({
      postcode,
      ...(data[postcode] || {})
    }));
  }

  return [];
}

function getFieldValue(record, fieldNames) {
  for (const field of fieldNames) {
    if (record && record[field] !== undefined && record[field] !== null && record[field] !== '') {
      return record[field];
    }
  }
  return undefined;
}

function getQuintile(record) {
  const value = getFieldValue(record, [
    'imdQuintile',
    'IMDQuintile',
    'imd_quintile',
    'quintile',
    'Quintile',
    'imd',
    'IMD'
  ]);
  const match = String(value || '').match(/\d+/);
  return match ? Number(match[0]) : null;
}

function getRecordPostcode(record) {
  return getFieldValue(record, [
    'postcode',
    'Postcode',
    'POSTCODE',
    'pcds',
    'pcd',
    'pcd2',
    'pcds_clean'
  ]);
}

export async function checkPostcode(postcode) {
  const normalized = normalizePostcode(postcode);

  if (!normalized) {
    throw new Error('Postcode is required');
  }

  if (!isValidUKPostcode(postcode)) {
    throw new Error('Please enter a valid UK postcode');
  }
const data = await loadImdData();

let match = data && data[normalized];

if (!match) {
  const records = toArray(data);
  match = records.find((record) => normalizePostcode(getRecordPostcode(record)) === normalized);
}
  const quintile = match ? getQuintile(match) : null;
  const eligible = typeof quintile === 'number' && quintile <= 2;
  const prefix = getPostcodePrefix(postcode);
  const formatted = formatPostcode(postcode);

  return {
    postcode: formatted,
    normalizedPostcode: normalized,
    found: Boolean(match),
    quintile,
    eligible,
    prefix,
    source: 'local-json',
    displayValue: formatted,
    note: eligible ? 'You live in an area that meets our contextual offer criteria.' : 'You do not live in an eligible area',
    message: match
      ? eligible
        ? 'Your postcode is in IMD quintile 1 or 2.'
        : 'Your postcode was found, but it is not in IMD quintile 1 or 2.'
      : 'We cannot find your current postcode. It may not be in our database. You can move on to the next question.'
  };
}

export default { checkPostcode };
